import React, { useCallback, useState } from "react";

import useFetchAndTrace, {
  FetchAndTraceErrorHandler,
} from "../../utils/hooks/fetchWithTrace";
import { CADSources } from "../../reducers/uploadedBoard";
import settings from "../../constants/settings";
import JSZip from "jszip";
import useTrackGoogleEvent, {
  UPLOAD_BOARD_EVENT,
} from "../../utils/observability/googleTagManager";
import {
  BOM_FILE_INFO_SCHEMA,
  GERBER_FILE_LAYER_MAPPING_SCHEMA,
  PNP_FILE_INFO_SCHEMA,
} from "./GerberFileIdentifier";
import { Formik } from "formik";
import * as Yup from "yup";
import UploadForm, { UPLOAD_MODES_DATA, UploadFormValues } from "./UploadForm";
import { useRouter } from "next/router";
import { trackUploadBoard } from "../../utils/observability/boardTracking";
import ErrorComponent from "../ErrorComponent";
import LoadingOverlay from "../LoadingOverlay";
import {
  S3FileUploadData,
  useUploadFileToS3,
} from "../../utils/hooks/uploadToS3";

interface KiCadFileUploadLinks {
  board_id: string;
  zip_file: S3FileUploadData;
}

export interface GerberFileUploadLinks {
  board_id: string;
  zip_file: S3FileUploadData;
}

function getProjectFromFile(fileName: string, fileExtension: string) {
  const regex = new RegExp(`^(.+)${fileExtension}$`);
  const match = fileName.match(regex);
  if (match && match.length >= 2) {
    return match[1];
  }
  return "New project";
}

export function getInitialValues(cadSource: CADSources) {
  return {
    cadSource: cadSource,
    gerberFileLayerMapping: null,
    bomFileInfo: null,
    pnpFileInfo: null,
    assemblyDrawing: null,
    fabDrawing: null,
    referenceFile: null,
    files: Array(UPLOAD_MODES_DATA[cadSource]!.requiredFiles.length).fill([]),
  };
}

export const uploadSchema = Yup.object().shape({
  cadSource: Yup.string().oneOf(Object.values(CADSources)).required(),
  files: Yup.array()
    .of(Yup.array().of(Yup.mixed()).required())
    .required()
    .test(
      "Files are valid",
      "Please select all necessary files.",
      (value, { parent }: { parent: UploadFormValues }) => {
        const requiredFiles =
          UPLOAD_MODES_DATA[parent.cadSource]!.requiredFiles;
        return requiredFiles.every((file, index) => {
          return file.multiple
            ? (value[index]?.length ?? 0) >= 1
            : value[index]?.length === 1;
        });
      },
    ),
  gerberFileLayerMapping: GERBER_FILE_LAYER_MAPPING_SCHEMA.nullable(),
  bomFileInfo: BOM_FILE_INFO_SCHEMA.nullable(),
  pnpFileInfo: PNP_FILE_INFO_SCHEMA.nullable(),
});

const UploadBoard = ({
  initialCADSource,
}: {
  initialCADSource: CADSources;
}) => {
  const router = useRouter();
  const fetchAndTrace = useFetchAndTrace();
  const [errored, setErrored] = useState(false);
  const [loading, setLoading] = useState(false);
  const trackGoogleEvent = useTrackGoogleEvent();

  const errorHandler: FetchAndTraceErrorHandler = useCallback(() => {
    setLoading(false);
    setErrored(true);
  }, []);

  const uploadFileToS3 = useUploadFileToS3(errorHandler);

  const onSubmit = (values: UploadFormValues) => {
    const uploadForCADSource = async (
      fileUploadLinks: KiCadFileUploadLinks,
    ) => {
      if (values.cadSource == CADSources.KICAD) {
        fileUploadLinks = fileUploadLinks as KiCadFileUploadLinks;
        const zip = new JSZip();
        values.files.forEach((fileType) => {
          fileType.forEach((file) => {
            zip.file(file.name, file);
          });
        });
        const content = await zip.generateAsync({ type: "blob" });
        return [uploadFileToS3(fileUploadLinks.zip_file, content)];
      } else if (values.cadSource == CADSources.GERBER) {
        fileUploadLinks = fileUploadLinks as GerberFileUploadLinks;
        return [uploadFileToS3(fileUploadLinks.zip_file, values.files[0]![0]!)];
      } else {
        throw Error("What cad source?");
      }
    };

    const uploadHandler = async (uploadResponse: Response) => {
      if (uploadResponse.ok) {
        const fileUploadLinks =
          (await uploadResponse.json()) as KiCadFileUploadLinks;

        await Promise.all(await uploadForCADSource(fileUploadLinks));

        const createBoardHandler = (createBoardResponse: Response) => {
          if (createBoardResponse.ok) {
            trackUploadBoard(fileUploadLinks.board_id, values.cadSource);
            trackGoogleEvent({ action: UPLOAD_BOARD_EVENT });
            void router.push({
              pathname: `/review-board/${fileUploadLinks.board_id}`,
            });
          } else {
            throw Error("Failed to create board");
          }
        };

        void fetchAndTrace(
          `${settings.api_url}/boards`,
          createBoardHandler,
          errorHandler,
          {
            method: "POST",
            body: JSON.stringify({
              board_id: fileUploadLinks.board_id,
              project_name: getProjectFromFile(
                values.files[0]![0]!.name,
                UPLOAD_MODES_DATA[values.cadSource]!.requiredFiles[0]!
                  .extension,
              ),
              cad_source: values.cadSource,
              gerber_file_layer_mapping: values.gerberFileLayerMapping,
              bom_file_info: values.bomFileInfo,
              pnp_file_info: values.pnpFileInfo,
              customer_uploaded_files: {
                assembly_drawings: values.assemblyDrawing,
                fab_drawings: values.fabDrawing,
                reference_files: values.referenceFile,
              },
            }),
          },
        );
      } else {
        throw Error("Failed to get signed links");
      }
    };

    setLoading(true);
    setErrored(false);
    void fetchAndTrace(
      `${settings.api_url}/boards/upload`,
      uploadHandler,
      errorHandler,
      {
        method: "POST",
        body: JSON.stringify({
          cad_source: values.cadSource,
        }),
      },
    );
  };

  if (errored) {
    return <ErrorComponent />;
  }

  return (
    <>
      <LoadingOverlay loading={loading} text={"Uploading board..."} />
      <Formik
        initialValues={getInitialValues(initialCADSource)}
        onSubmit={onSubmit}
        validationSchema={uploadSchema}
      >
        <UploadForm loading={loading} />
      </Formik>
    </>
  );
};

export default UploadBoard;
